import { useEffect, useEffectEvent, useLayoutEffect, useRef } from "react";
import { type Atom, useCreateAtom } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { isEqual } from "@common/helpers/comparison";
import type { DataGridReactTable, UseDataGridOptions } from "../../types";

interface UseAllMatchingSelectionInput<TData extends RowData> {
    table: DataGridReactTable<TData>;
    data: readonly TData[];
    /** `loadMore.resetKey`: a new key is a new matching set. */
    resetKey: string | undefined;
    optionsRef: { readonly current: UseDataGridOptions<TData> };
}

/**
 * The "select all matching" flag for server-paged tables. While it is on,
 * every loaded row stays selected (new pages included). It turns off when a
 * row is deselected or the matching set changes (a new `loadMore.resetKey`,
 * search, or column filter); the consumer clears its own row selection.
 * Values are compared, not identities, so a controlled filter rebuilt each
 * render keeps the flag.
 */
export const useAllMatchingSelection = <TData extends RowData>({
    table,
    data,
    resetKey,
    optionsRef,
}: UseAllMatchingSelectionInput<TData>): Atom<boolean> => {
    const allMatchingSelected = useCreateAtom(false);
    const { globalFilter, columnFilters } = table.state;
    const rowSelectionAtom = table.atoms.rowSelection;
    const matchingSetRef = useRef({ resetKey, globalFilter, columnFilters });
    // `table` changes identity with root state; these read it without re-running the effects.
    const clearOnPartialSelection = useEffectEvent(() => {
        if (
            allMatchingSelected.get() &&
            table.getFilteredRowModel().flatRows.length > 0 &&
            !table.getIsAllRowsSelected()
        ) {
            allMatchingSelected.set(false);
        }
    });
    const selectAllLoadedRows = useEffectEvent(() => {
        if (!table.getIsAllRowsSelected()) {
            table.toggleAllRowsSelected(true);
        }
    });

    useEffect(() => {
        const selectionSubscription = rowSelectionAtom.subscribe(() =>
            clearOnPartialSelection(),
        );
        const flagSubscription = allMatchingSelected.subscribe(() =>
            optionsRef.current.rowSelection?.onAllMatchingChange?.(
                allMatchingSelected.get(),
            ),
        );
        return () => {
            selectionSubscription.unsubscribe();
            flagSubscription.unsubscribe();
        };
    }, [allMatchingSelected, optionsRef, rowSelectionAtom]);

    // Runs before paint, so rows of a new matching set are never auto-selected
    // and a new page under "all matching" is selected before it shows.
    useLayoutEffect(() => {
        const previous = matchingSetRef.current;
        const matchingSet = { resetKey, globalFilter, columnFilters };
        matchingSetRef.current = matchingSet;
        if (!allMatchingSelected.get()) {
            return;
        }
        if (!isEqual(previous, matchingSet)) {
            allMatchingSelected.set(false);
            return;
        }
        if (data.length > 0) {
            selectAllLoadedRows();
        }
    }, [allMatchingSelected, data, resetKey, globalFilter, columnFilters]);

    return allMatchingSelected;
};
